import React, { useEffect, useState } from "react";
import { DataGrid, GridToolbarContainer } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import PageHeader from "../../../components/PageHeader";
import * as invitationService from "../services/InvitationService";
import { useNavigate } from "react-router-dom";
import Notification from "../../../components/mui/Notification";
import useMediaQuery from "@mui/material/useMediaQuery";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { useTheme } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import { INSTRUCTOR_BOARD_ROUTE } from "../helper/Constants";
import { useTranslation } from "react-i18next";

export default function InvitationList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [openInvitationDialog, setOpenInvitationDialog] = useState(false);
  const [loadingInvitation, setLoadingInvitation] = useState();
  const [invitationError, setInvitationError] = useState();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    invitationService
      .getAll()
      .then((response) => {
        setRecords(response.data);
      })
      .catch((e) => {
        console.error(`Error: ${e.message}`);
        setNotify({
          isOpen: true,
          message: "Could not connect to server. Please try again later.",
          type: "error",
        });
      })
      .finally(() => setLoading(false));
  }, [refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  function CustomToolbar() {
    return (
      <Box sx={{ m: 1, display: "flex", justifyContent: "flex-end" }}>
        <GridToolbarContainer>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="flex-start"
            onClick={() => {
              setOpenInvitationDialog(true);
            }}
          >
            <IconButton color="success" size="small">
              <AddIcon />
            </IconButton>
            <Typography
              sx={{
                cursor: "pointer",
                color: "success.main", // Use MUI success color
              }}
              variant={"button"}
            >
              {t("invitation_new")}
            </Typography>
          </Box>
          <Typography
            onClick={() => navigate(INSTRUCTOR_BOARD_ROUTE)}
            sx={{ cursor: "pointer", ml: 2 }}
            variant={"button"}
          >
            {t("invitation_back")}
          </Typography>
        </GridToolbarContainer>
      </Box>
    );
  }

  const handleCloseInvitationDialog = () => {
    setOpenInvitationDialog(false);
  };

  const onRefresh = () => {
    setRefresh(!refresh);
  };

  const columns = [
    { field: "recipient", headerName: t("invitation_recipient"), flex: 1, minWidth: 100 },
    {
      field: "count",
      headerName: t("invitation_nb_attempts"),
      flex: 1,
      minWidth: 80,
    },
    {
      field: "status",
      headerName: t("status"),
      flex: 1,
      minWidth: 80,
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <PageHeader title={t("invitation_title")} onRefresh={() => onRefresh()} />
      <DataGrid
        key={records.length}
        loading={loading}
        autoHeight
        rows={records}
        columns={columns}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
        }
        disableColumnResize
        density="compact"
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          filterPanel: {
            filterFormProps: {
              logicOperatorInputProps: {
                variant: "outlined",
                size: "small",
              },
              columnInputProps: {
                variant: "outlined",
                size: "small",
                sx: { mt: "auto" },
              },
              operatorInputProps: {
                variant: "outlined",
                size: "small",
                sx: { mt: "auto" },
              },
              valueInputProps: {
                InputComponentProps: {
                  variant: "outlined",
                  size: "small",
                },
              },
            },
          },
        }}
      />
      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />

      <Dialog
        fullScreen={fullScreen}
        open={openInvitationDialog}
        onClose={handleCloseInvitationDialog}
        PaperProps={{
          component: "form",
          onSubmit: (event) => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);
            const formJson = Object.fromEntries(formData.entries());
            const email = formJson.email?.trim();
            if (!email) {
              setInvitationError("Email Address cannot be empty.");
              return;
            } else if (!/$^|.+@.+..+/.test(email)) {
              setInvitationError("Email Address is not valid.");
              return;
            }
            setLoadingInvitation(true);
            invitationService
              .invite(email)
              .then((res) => {
                // setRecords((prevRecords) => [res.data, ...prevRecords]);
                onRefresh();
                setNotify({
                  isOpen: true,
                  message: "Successfully sent an invitation.",
                  type: "success",
                });
              })
              .catch((e) => {
                console.error(`Error: ${e.message}`);
              })
              .finally(() => {
                setLoadingInvitation(false);
                handleCloseInvitationDialog();
              });
          },
        }}
      >
        <DialogTitle>{t("invitation_dialog_title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("invitation_dialog_text")}
          </DialogContentText>
          <TextField
            autoFocus
            required
            margin="dense"
            id="email"
            name="email"
            label={t("form_email_address")}
            fullWidth
            variant="standard"
            error={Boolean(invitationError)}
            helperText={invitationError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInvitationDialog}>{t("form_cancel")}</Button>
          <LoadingButton loading={loadingInvitation} type="submit">
            {t("form_send")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
